import React, { ReactNode, useCallback, useMemo, useState } from 'react';
import { Entity } from '@shared/data/entity/Entity';
import { createCtx } from '@shared/util/context';
import { useBoolean } from '@shared/util/hooks';

// https://github.com/coast-app/core-api/blob/ec802e05740f83950034e2b843c303e751112421/packages/core-models/src/application/validation/Constants.ts#L12-L13
export const MAX_SELECTED_ENTITIES = 100;

interface EntitySelectionContextValue {
  selectedEntities: Entity[];
  selectedCount: number;
  addEntity(entity: Entity): void;
  removeEntity(id: MBAPI.Scalar.UUID): void;
  toggleEntity(entity: Entity): void;
  isEntitySelected(id: MBAPI.Scalar.UUID): boolean;
  selectionActive: boolean;
  reset(): void;
  loading: boolean;
  startLoading(): void;
  stopLoading(): void;
}

const [useEntitySelection, ContextProvider] = createCtx<EntitySelectionContextValue>();

export { useEntitySelection };

export function EntitySelectionProvider({ children }: { children: ReactNode }) {
  const [selectedEntities, setSelectedEntities] = useState<Entity[]>([]);
  const [loading, startLoading, stopLoading] = useBoolean();

  const addEntity = useCallback((entity: Entity) => {
    setSelectedEntities(prev => {
      if (prev.length >= MAX_SELECTED_ENTITIES) {
        return prev;
      }
      return [...prev.filter(item => item.id !== entity.id), entity];
    });
  }, []);

  const removeEntity = useCallback((id: MBAPI.Scalar.UUID) => {
    setSelectedEntities(prev => prev.filter(item => item.id !== id));
  }, []);

  const reset = useCallback(() => {
    setSelectedEntities([]);
    stopLoading();
  }, [stopLoading]);

  const isEntitySelected = useCallback(
    (id: MBAPI.Scalar.UUID) => {
      return selectedEntities.some(item => item.id === id);
    },
    [selectedEntities],
  );

  const toggleEntity = useCallback(
    (entity: Entity) => {
      if (isEntitySelected(entity.id)) {
        removeEntity(entity.id);
      } else {
        addEntity(entity);
      }
    },
    [addEntity, removeEntity, isEntitySelected],
  );

  const value = useMemo(() => {
    const selectedCount = selectedEntities.length;
    const selectionActive = selectedCount > 0;

    return {
      selectedEntities,
      selectedCount,
      selectionActive,
      addEntity,
      removeEntity,
      toggleEntity,
      isEntitySelected,
      reset,
      loading,
      startLoading,
      stopLoading,
    };
  }, [
    selectedEntities,
    addEntity,
    removeEntity,
    toggleEntity,
    reset,
    loading,
    startLoading,
    stopLoading,
    isEntitySelected,
  ]);

  return <ContextProvider value={value}>{children}</ContextProvider>;
}
